import React, { Component } from 'react'
import { number, func, any } from 'prop-types'
import { injectIntl } from 'react-intl'
import { Progress } from 'antd'
import IdleTimer from 'react-idle-timer'
import EventsService from 'src/utils/events-service'
import {
  getSessionTimeout,
  isEmpty,
  isInStandaloneMode,
  extendLoginToken,
  getLoginToken,
  getKioskDocument,
  timeSinceTimestamp,
  getLoginExtendTimeout,
} from 'src/utils/helpers'
import {
  idleDetectionEvents,
} from '../helpers'
import { FooterActions } from 'src/components/Layout/Footer/FooterActions'
import Button from 'src/components/Button'
import intlMessagesAuth from 'src/modules/Auth/translations'
import styles from './styles.module.less'

class IdleLogOut extends Component {
  static propTypes = {
    idleTimeoutSeconds: number.isRequired,
    allowExtendSeconds: number.isRequired,
    user: any,
    resetApp: func.isRequired,
    resetUser: func.isRequired,
  }

  static defaultProps = {
    // Show popup after 15 min (or something else if set in local storage - for debugging)
    idleTimeoutSeconds: getSessionTimeout() * 60,
    // Give 1 min to allow extending the session
    allowExtendSeconds: getLoginExtendTimeout(),
  }

  constructor(props) {
    super(props)

    this.state = {
      isModalVisible: false,
      // Timestamp used to calculate seconds left to extend session
      timeoutStart: 0,
    }

    this.extendSessionTimer = 0
    this.startExtendSessionTimer = this.startExtendSessionTimer.bind(this)
    this.countDown = this.countDown.bind(this)

    this.onAction = this._onAction.bind(this)
    this.onIdle = this._onIdle.bind(this)
  }

  componentDidMount() {
    this.logOutIfTokenExpired()
  }

  componentDidUpdate() {
    this.logOutIfTokenExpired()
  }

  /**
   * If "extend session" countdown not already running, reset it and start new one.
   */
  startExtendSessionTimer() {
    const extendLeft = this.getExtendSessionTimeoutSeconds()

    if (this.extendSessionTimer === 0 && extendLeft > 0) {
      this.clearExtendSessionTimer(() => {

        // Save current timestamp (in ms) when timeout starts
        this.setState({
          timeoutStart: new Date().getTime(),
        })

        // Start countdown
        this.extendSessionTimer = setInterval(this.countDown, 1000)
      })
    }
  }

  /**
   * Run additional countdown for allowing log in session to be extended.
   *
   * If not canceled, it will logout when countdown comes to the end.
   */
  countDown() {
    // Exit countdown if user is no longer logged in
    const willLogOut = this.logOutIfTokenExpired()
    if(willLogOut === true) {
      return true
    }

    // Get seconds currently left for extending the session
    const extendLeft = this.getExtendSessionTimeoutSeconds()

    // Update state to see countdown seconds
    this.forceUpdate()

    // When "extend session" countdown comes to an end
    if (extendLeft <= 0) {

      // Hide popup
      this.setState({
        isModalVisible: false,
      })

      // Clear "extend session" timeout
      this.clearExtendSessionTimer(() => {
        // Do actual logout and app reset
        this.logOut()
      })
    }
  }

  /**
   * Log out if token (cookie expired)
   *
   * Used when component is mounted or updated (e.g. re-opened browser on iOS)
   *
   * @returns {boolean} Will it log out or not
   */
  logOutIfTokenExpired = () => {
    // Logout if user is still set but token was removed from cookie (when we re-open the app).
    const token = getLoginToken()
    if(!isEmpty(this.props.user) && !token) {

      this.logOut()
      return true
    }
    return false
  }

  /**
   * Do actual logout and app reset.
   */
  logOut = async () => {
    const { userLastAccessType, resetUser, resetApp, isWidgetState, history } = this.props

    // Register logout event only if we know who is logging out
    if (getLoginToken()) {
      const logOutEventType = userLastAccessType === 'logInAsKioskUserLink' ? 'logOutAsKioskUser' : 'logOutAuto'

      await EventsService.registerEvent({isWidget: isWidgetState}, 'access', logOutEventType)
    }

    resetUser()
    resetApp('logOut', isWidgetState, history)
  }

  /**
   * Reset "extend session" countdown.
   */
  clearExtendSessionTimer = (callback) => {
    if(this.extendSessionTimer) {
      clearInterval(this.extendSessionTimer)
      this.extendSessionTimer = 0
    }

    this.setState({
      timeoutStart: 0,
    }, () => {
      if(callback && typeof callback === 'function') {
        callback()
      }
    })
  }

  /**
   * Cancel auto log out and extend the session.
   *
   * @param e Click even
   */
  extendSession = (e) => {
    e.preventDefault()

    // Should be handled with _onAction, but extend here as well (just in case)
    extendLoginToken()

    // Hide popup
    this.setState({
      isModalVisible: false,
    })

    // Clear "extend session" countdown
    this.clearExtendSessionTimer()
  }

  /**
   * Reset login token in cookie to extend its expiry date.
   *
   * @param e
   * @private
   */
  _onAction(e) {
    extendLoginToken()
  }

  /**
   * When app is not active show popup and start "extend session" countdown.
   *
   * @param e
   * @private
   */
  _onIdle(e) {
    const token = getLoginToken()
    if(token && !isInStandaloneMode()) {
      // Show popup
      this.setState({
        isModalVisible: true,
      }, () => {

        // Start "extend session" countdown
        this.startExtendSessionTimer()

      })
    }
  }


  /**
   * Takes saved timestamp when timeout started and calculate current timeout in seconds
   *
   * @returns {integer} Seconds left to extend session
   */
  getExtendSessionTimeoutSeconds = () => {
    const {
      allowExtendSeconds,
    } = this.props

    const {
      timeoutStart,
    } = this.state

    // Calculate seconds since "extend session" timeout started
    const secondsSinceTimestamp = timeSinceTimestamp(timeoutStart)

    // Calculate seconds left for "extend session" timeout (allowed minus current)
    return secondsSinceTimestamp > 0 && allowExtendSeconds > 0 ?
      (allowExtendSeconds - secondsSinceTimestamp) : allowExtendSeconds
  }

  render() {
    const {
      intl,
      idleTimeoutSeconds,
      allowExtendSeconds,
    } = this.props

    const {
      isModalVisible,
    } = this.state

    // Disable this completely if not logged in or is standalone app or in local development environment
    const token = getLoginToken()
    if(!token || isInStandaloneMode()) {
      return ''
    }

    // Seconds left to extend session
    const extendLeft = this.getExtendSessionTimeoutSeconds()

    // Calculate percentage left for "extend session" timeout (for visualization)
    const extendPercent = extendLeft > 0 && allowExtendSeconds > 0 ? parseInt((extendLeft / allowExtendSeconds) * 100) : 100

    return (
      <div>
        <IdleTimer
          // Required for widget to detect events within the widget instead of parent site
          element={getKioskDocument()}
          debounce={250}
          // This is where everything starts, when page is idle (not used)
          onIdle={this.onIdle}
          // Extend login token on user actions
          onAction={this.onAction}
          // Seconds to milliseconds
          timeout={1000 * idleTimeoutSeconds}
          // For which user actions to extend the session
          events={idleDetectionEvents}
        />
        {isModalVisible ? (
          <div className={styles.modal}>
            <div className={styles.modalCentered}>
              <div className={styles.modalContent}>
                <div>
                  <h3 className={styles.modalTitle}>
                    {intl.formatMessage(intlMessagesAuth.authAutoLogOutMessage)}
                  </h3>

                  <div className={styles.progressHolder + " kiosk-color--primary-color"}>
                    <Progress
                      type="circle"
                      width={100}
                      strokeWidth={2}
                      percent={extendPercent}
                      showInfo={false}
                    />
                    <div className={styles.progressValue}>{extendLeft}s</div>
                  </div>

                  <FooterActions>
                    <Button type="primary" onClick={this.extendSession}>
                      {intl.formatMessage(intlMessagesAuth.authAutoLogOutExtendButton)}
                    </Button>
                  </FooterActions>
                </div>
              </div>
            </div>
          </div>
        ) : ''}
      </div>
    )
  }
}

export default injectIntl(IdleLogOut)